import { Store } from 'stores/type';
import { logger, delay } from 'utils';
import { save, load } from 'helpers/storage';
import EventsManager from 'managers/events.manager';
import { Firebase } from './types';

const STORAGE_KEY = '__fcm';
const DELAY = 1.5 * 1000;
const MIN_SESSIONS_COUNTS = 3;

class FirebaseNotifications {
  private firebase!: Firebase;
  private _saveToken!: ((token: string) => Promise<void>);

  public init(firebase: Firebase, $store: Store, saveToken: typeof FirebaseNotifications['prototype']['_saveToken']) {
    this.firebase = firebase;
    this._saveToken = saveToken;

    this.initEvents();

    EventsManager.once('PLAY', async () => {
      await delay(DELAY);

      if (!this.shouldLoad($store)) return;

      await this.load();
      this.onLoaded();
      if (this.shouldRequestPermission($store)) {
        this.request();
      }
    });
  }
  private initEvents() {
    EventsManager.on('NOTIFICATIONS_REQUEST', async () => {
      await this.load();
      this.onLoaded();
      this.request();
    });
  }

  private load() {
    // @ts-ignore
    return import(/* webpackChunkName: "firebase-messaging" */ 'firebase/messaging');
  }
  private onLoaded() {
    this.handleTokenRefresh();
    this.messaging.onMessage(this.onMessage);
  }

  private handleTokenRefresh() {
    this.messaging.onTokenRefresh(async () => {
      const token = await this.getToken();
      this.sendToken(token);
    });
  }
  private onMessage = (payload: any) => {
    EventsManager.emit('NOTIFICATION_RECEIVED', payload);
    this.log('message received', payload);
  }

  public async request() {
    if (this.hasSavedToken || this.hasDenied) return;

    await this.requestPermission();
    const token = await this.getToken();
    this.sendToken(token);
    this.notifySuccess();
  }
  public requestPermission() {
    return this.messaging.requestPermission();
  }

  public async getToken(): Promise<string> {
    const token = await this.messaging.getToken();
    if (!token) throw new Error();

    return token;
  }
  private async sendToken(token: string) {
    if (typeof this._saveToken !== 'function') return;

    await this._saveToken(token);
    save(STORAGE_KEY, JSON.stringify(1));
  }

  private notifySuccess() {
    EventsManager.emit('NOTIFICATIONS_APPROVED');
  }

  private shouldLoad(store: Store) {
    return this.shouldRequestPermission(store)
      || ((window as any).Notification && Notification.permission === 'granted');
  }
  private shouldRequestPermission(store: Store) {
    return store.getters.sessionsCount >= MIN_SESSIONS_COUNTS && !this.hasDenied;
  }
  private get messaging() {
    return this.firebase.messaging();
  }
  private get hasSavedToken() {
    return !!load(STORAGE_KEY);
  }
  // @ts-ignore
  private get canRequest() {
    return !this.hasSavedToken && !this.hasDenied;
  }
  private get hasDenied() {
    return !(window as any).Notification || Notification.permission === 'denied';
  }

  private log(...messages: any[]) {
    logger('firebase', '[NOTIFICATIONS]', ...messages);
  }
}

export default FirebaseNotifications;
